import { createFileRoute, Link } from "@tanstack/react-router";
import { allPosts, allTutorials } from "content-collections";

export const Route = createFileRoute("/_layout/blog/")({
	component: RouteComponent,
	head: () => {
		const blogListSchema = {
			"@context": "https://schema.org",
			"@type": "Blog",
			name: "Macrowave Blog",
			description:
				"Insights and guides on live broadcasting and audio streaming",
			url: "https://macrowave.co/blog",
			blogPost: allPosts.map((post) => ({
				"@type": "BlogPosting",
				headline: post.title,
				description: post.summary,
				url: `https://macrowave.co/blog/${post._meta.path}`,
				image: post.image ? `https://macrowave.co${post.image}` : undefined,
			})),
		};

		return {
			meta: [
				{
					name: "description",
					content:
						"Insights and guides on live broadcasting and audio streaming with Macrowave",
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify(blogListSchema),
				},
			],
		};
	},
});

function RouteComponent() {
	return (
		<div className="max-w-7xl mx-auto py-16 px-6">
			<h1 className="font-serif-600 text-4xl text-black mb-12">Blog</h1>
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-24">
				{allPosts.map((post) => (
					<Link
						key={post._meta.path}
						to={`/blog/${post._meta.path}`}
						className="group block"
					>
						<article className="h-full flex flex-col">
							{post.image && (
								<div className="relative aspect-[16/10] mb-6 overflow-hidden rounded-2xl bg-gray-100">
									<img
										src={post.image}
										alt={post.title}
										loading="lazy"
										className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
									/>
								</div>
							)}
							<div className="flex flex-col flex-grow">
								<div className="flex items-center justify-between mb-3">
									<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
										Article
									</span>
									<time
										className="font-serif text-xs text-gray-500"
										dateTime={post.publishedAt}
									>
										{new Date(post.publishedAt).toLocaleDateString("en-US", {
											year: "numeric",
											month: "long",
											day: "numeric",
										})}
									</time>
								</div>
								<h2 className="font-serif-600 text-2xl text-black mb-3 leading-tight group-hover:text-gray-600 transition-colors">
									{post.title}
								</h2>
								<p className="font-serif text-gray-600 leading-relaxed flex-grow">
									{post.summary}
								</p>
							</div>
						</article>
					</Link>
				))}
			</div>

			{/* Tutorials Section */}
			<section className="border-t border-gray-200 pt-16">
				<div className="flex items-center justify-between mb-8">
					<h2 className="font-serif-600 text-3xl text-black">
						Embed Tutorials
					</h2>
					<Link
						to="/tutorials"
						className="text-sm text-gray-600 hover:text-black transition-colors"
					>
						View all →
					</Link>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{allTutorials.slice(0, 6).map((tutorial) => (
						<Link
							key={tutorial._meta.path}
							to={`/tutorials/${tutorial._meta.path}`}
							className="group block"
						>
							<article className="h-full flex flex-col">
								{tutorial.image && (
									<div className="relative aspect-[16/10] mb-4 overflow-hidden rounded-xl bg-gray-100">
										<img
											src={tutorial.image}
											alt={tutorial.title}
											loading="lazy"
											className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
										/>
									</div>
								)}
								<div className="flex flex-col flex-grow">
									<div className="flex items-center justify-between mb-2">
										<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
											{tutorial.platform}
										</span>
										<span className="font-serif text-xs text-gray-500">
											{tutorial.readingTime} min
										</span>
									</div>
									<h3 className="font-serif-600 text-lg text-black mb-2 leading-tight group-hover:text-gray-600 transition-colors">
										{tutorial.title}
									</h3>
									<p className="font-serif text-sm text-gray-600 leading-relaxed flex-grow">
										{tutorial.summary}
									</p>
								</div>
							</article>
						</Link>
					))}
				</div>
			</section>
		</div>
	);
}
